import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from 'shared/components/ui/alert-dialog';
import { AmountInput } from 'shared/components/ui/AmountInput';
import { Label } from 'shared/components/ui/label';
import { IconNote } from 'shared/components/ui/IconNote';
import { AssistantService } from 'shared/services/AssistantService';
import { ReportSectionsTree, ReportSections } from '../../single/components/ReportSectionsTree';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';
import colorsStore from 'shared/store/colorsStore';
import useThreadsStore from 'shared/store/threadsStore';
import { createComparisonReportParameters } from 'utils/comparisonUtils';
import { useComparisonLabels } from '../ComparisonLabelsContext';
import { createLogger } from 'shared/logger';

const log = createLogger('GenerateComparisonReport');

type GenerateComparisonReportProps = {
  threadId: string;
  portfolioId1: string;
  portfolioId2: string;
  countervalueTotal1?: number;
  countervalueTotal2?: number;
  commentoAlPortafoglio1?: boolean;
  commentoAlPortafoglio2?: boolean;
  children: React.ReactNode;
  onGenerate: (data: any, fromReport?: boolean) => void;
  disabled?: boolean;
};

export const GenerateComparisonReport = forwardRef<{ generate: () => void }, GenerateComparisonReportProps>(
  (
    {
      threadId,
      portfolioId1,
      portfolioId2,
      countervalueTotal1,
      countervalueTotal2,
      commentoAlPortafoglio1,
      commentoAlPortafoglio2,
      children,
      onGenerate,
      disabled,
    },
    ref,
  ) => {
    const colorsConfig = colorsStore((state) => state.colorsConfigStore);

    // Access story from global store
    const stories = useThreadsStore((state) => state.stories);
    const fetchStory = useThreadsStore((state) => state.fetchStory);

    // Polling ref
    const pollingRef = useRef<NodeJS.Timeout | null>(null);

    // Helper to check if portfolio_story section is selected
    const isPortfolioStorySelected = () => {
      return reportSections.portfolio_story?.visible && reportSections.portfolio_story?.selected;
    };

    // Helper to get current story from the global store for a portfolio
    const getCurrentStory = (portfolioId: string) => {
      if (!threadId || !portfolioId) return null;
      return stories?.[threadId]?.[portfolioId] ?? null;
    };

    // Helper to check if story is available (null/undefined = still loading, anything else = done)
    const isStoryAvailable = (portfolioId: string) => {
      const story = getCurrentStory(portfolioId);
      return story !== null && story !== undefined;
    };

    // Check if PDF generation should be disabled
    // Only disable if commentoAlPortafoglio is true for either portfolio, portfolio_story is selected,
    // and the corresponding story is not available
    const isPdfGenerationDisabled = () => {
      if (!isPortfolioStorySelected()) return false;

      const needsStory1 = commentoAlPortafoglio1 && !isStoryAvailable(portfolioId1);
      const needsStory2 = commentoAlPortafoglio2 && !isStoryAvailable(portfolioId2);

      return needsStory1 || needsStory2;
    };

    const { t } = useTranslation();
    const labels = useComparisonLabels();
    const [bankerName, setBankerName] = useState('');
    const [bankerRole, setBankerRole] = useState('');
    const hasCountervalue1 = countervalueTotal1 != null && countervalueTotal1 > 0;
    const hasCountervalue2 = countervalueTotal2 != null && countervalueTotal2 > 0;
    const [proposedValue, setProposedValue] = useState<number | undefined>(
      hasCountervalue1 ? countervalueTotal1 : undefined,
    );
    const [proposedValue2, setProposedValue2] = useState<number | undefined>(
      hasCountervalue2 ? countervalueTotal2 : undefined,
    );
    const [reportSections, setReportSections] = useState<ReportSections>({});
    const [showCosts, setShowCosts] = useState(true);
    const [showIsin, setShowIsin] = useState(true);

    // Sync proposedValue when countervalueTotal1 changes (late prop arrival)
    useEffect(() => {
      if (countervalueTotal1 != null && countervalueTotal1 > 0) {
        setProposedValue(countervalueTotal1);
      }
    }, [countervalueTotal1]);

    // Sync proposedValue2 when countervalueTotal2 changes (late prop arrival)
    useEffect(() => {
      if (countervalueTotal2 != null && countervalueTotal2 > 0) {
        setProposedValue2(countervalueTotal2);
      }
    }, [countervalueTotal2]);

    useImperativeHandle(ref, () => ({
      generate: () => {
        handleGenerate(false);
      },
    }));

    useEffect(() => {
      const loadReportParameters = async () => {
        try {
          let response;

          if (portfolioId1 && portfolioId2) {
            // Create enriched comparison parameters using v2 API
            response = await createComparisonReportParameters(
              (portfolioId: string) => AssistantService.fetchReportParameters(portfolioId),
              portfolioId1,
              portfolioId2,
            );
          } else {
            log.warn('no portfolio IDs available for comparison report parameters');
            // Use default parameters
            response = { banker_name: '', banker_role: '', report_sections: {} };
          }

          setBankerName(response.banker_name || '');
          setBankerRole(response.banker_role || '');
          setShowCosts(response.show_instrument_costs ?? true);
          setShowIsin(response.show_isin ?? true);

          // Process sections to add hidden flag
          const processedSections = JSON.parse(JSON.stringify(response.report_sections || {}));
          // «Espandi Wrapper» (issue #90) exists only in the single-portfolio
          // report; the comparison PDF has no Wrapper pages, so hide the flag
          // (visible=false also keeps the backend from loading the data).
          if (processedSections.portafoglio?.espandi_wrapper) {
            processedSections.portafoglio.espandi_wrapper.visible = false;
            processedSections.portafoglio.espandi_wrapper.selected = false;
          }
          setReportSections(processedSections);
        } catch (error) {
          log.error('load comparison report parameters failed', { error });
        }
      };

      loadReportParameters();
    }, [threadId, portfolioId1, portfolioId2]);

    // Start/stop polling when portfolio_story selection changes
    // Only poll for portfolios that have commentoAlPortafoglio enabled
    useEffect(() => {
      const shouldPoll = isPortfolioStorySelected() && isPdfGenerationDisabled();

      if (shouldPoll && threadId) {
        // Initial fetch for each portfolio that needs a story
        if (commentoAlPortafoglio1 && portfolioId1 && !isStoryAvailable(portfolioId1)) {
          fetchStory(threadId, portfolioId1);
        }
        if (commentoAlPortafoglio2 && portfolioId2 && !isStoryAvailable(portfolioId2)) {
          fetchStory(threadId, portfolioId2);
        }

        // Start polling every 5 seconds
        pollingRef.current = setInterval(() => {
          if (commentoAlPortafoglio1 && portfolioId1 && !isStoryAvailable(portfolioId1)) {
            fetchStory(threadId, portfolioId1);
          }
          if (commentoAlPortafoglio2 && portfolioId2 && !isStoryAvailable(portfolioId2)) {
            fetchStory(threadId, portfolioId2);
          }
        }, 5000);
      }

      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      };
    }, [
      commentoAlPortafoglio1,
      commentoAlPortafoglio2,
      reportSections.portfolio_story?.selected,
      threadId,
      portfolioId1,
      portfolioId2,
      stories,
    ]);

    const handleGenerate = (fromReport: boolean = false) => {
      const data = {
        banker_name: bankerName,
        banker_role: bankerRole,
        portfolio_id_1: portfolioId1,
        portfolio_id_2: portfolioId2,
        proposed_value: proposedValue,
        proposed_value_2: proposedValue2,
        show_instrument_costs: showCosts,
        show_isin: showIsin,
        ...(fromReport && { report_sections: reportSections }),
        ...(labels.portfolio1 !== t('portafoglioCompare.portafoglio1') && {
          portfolio_labels: [labels.portfolio1, labels.portfolio2],
          portfolio_short_labels: [labels.portfolio1Short, labels.portfolio2Short],
        }),
      };
      onGenerate(data, fromReport);
    };

    const hasSelectedSections = () => {
      return Object.values(reportSections).some((section) => section.visible && section.selected);
    };

    const [open, setOpen] = useState(false);
    return (
      <AlertDialog
        open={open}
        onOpenChange={(isOpen) => {
          if (disabled && isOpen) return;
          setOpen(isOpen);
          if (!isOpen && pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }}
      >
        <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
        <AlertDialogContent
          style={{
            backgroundColor: 'white',
            color: colorsConfig.threadHistoryCopy || '#ffffff',
            maxWidth: '600px',
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: colorsConfig.finecoBlack }}>
              {t('generateReport.titleComparison')}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="bankerRole" style={{ color: colorsConfig.finecoBlack }}>
                      {t('generateReport.bankerRole')}
                    </Label>
                    <select
                      id="bankerRole"
                      value={bankerRole}
                      onChange={(e) => setBankerRole(e.target.value)}
                      className="w-full rounded border p-2"
                      style={{ color: colorsConfig.finecoBlack }}
                    >
                      <option value="Personal Financial Advisor">Personal Financial Advisor</option>
                      <option value="Private Banker">Private Banker</option>
                      <option value="Senior Private Banker">Senior Private Banker</option>
                      <option value="Senior Private Banker e Ambassador">Senior Private Banker e Ambassador</option>
                      <option value="Group Manager">Group Manager</option>
                      <option value="Area Manager">Area Manager</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="proposedValue" style={{ color: colorsConfig.finecoBlack }}>
                      {hasCountervalue1 ? t('generateReport.totalPortfolioValue') : t('generateReport.proposedValue')} ({labels.portfolio1})
                    </Label>
                    <AmountInput
                      value={proposedValue || ''}
                      onChangeValue={(v) => setProposedValue(parseFloat(v) || 0)}
                      className="h-10 w-full rounded-xl border px-3 py-2 text-sm text-right"
                      disabled={hasCountervalue1}
                      style={{
                        backgroundColor: hasCountervalue1 ? '#f5f5f5' : colorsConfig.white,
                        color: colorsConfig.finecoBlack,
                        borderColor: colorsConfig.gray2,
                        ...(hasCountervalue1 && { cursor: 'not-allowed' }),
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="proposedValue2" style={{ color: colorsConfig.finecoBlack }}>
                      {hasCountervalue2 ? t('generateReport.totalPortfolioValue') : t('generateReport.proposedValue')} ({labels.portfolio2})
                    </Label>
                    <AmountInput
                      value={proposedValue2 || ''}
                      onChangeValue={(v) => setProposedValue2(parseFloat(v) || 0)}
                      className="h-10 w-full rounded-xl border px-3 py-2 text-sm text-right"
                      disabled={hasCountervalue2}
                      style={{
                        backgroundColor: hasCountervalue2 ? '#f5f5f5' : colorsConfig.white,
                        color: colorsConfig.finecoBlack,
                        borderColor: colorsConfig.gray2,
                        ...(hasCountervalue2 && { cursor: 'not-allowed' }),
                      }}
                    />
                  </div>
                </div>
                <div>{/* Empty second column */}</div>
              </div>

              <div className="mt-8">
                <h3 className="mb-4 font-medium" style={{ color: colorsConfig.finecoBlack }}>
                  {t('generateReport.sections')}
                </h3>
                <ReportSectionsTree
                  sections={reportSections}
                  onChange={setReportSections}
                  showCosts={showCosts}
                  onShowCostsChange={setShowCosts}
                  showIsin={showIsin}
                  onShowIsinChange={setShowIsin}
                />

                {/* Story status message */}
                {isPortfolioStorySelected() && (commentoAlPortafoglio1 || commentoAlPortafoglio2) && (
                  <div className="mt-4">
                    {isPdfGenerationDisabled() ? (
                      <IconNote
                        type="attention"
                        title={t('generateReport.storyGenerating')}
                        body={t('generateReport.storyGeneratingDescription')}
                      />
                    ) : (
                      <IconNote
                        type="important"
                        title={t('generateReport.storyAvailable')}
                        body={t('generateReport.storyAvailableDescription')}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <FinecoButton
              click={() => {
                setOpen(false);
              }}
              variant="white"
              copy={t('global.cancel').toUpperCase()}
            />
            <FinecoButton
              disabled={!hasSelectedSections() || isPdfGenerationDisabled()}
              click={() => {
                handleGenerate(true);
                setOpen(false);
              }}
              copy={
                isPdfGenerationDisabled()
                  ? t('generateReport.waitingForStory').toUpperCase()
                  : t('generateReport.generate').toUpperCase()
              }
            />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },
);
